import { type ReactNode, createContext, useContext, useEffect, useRef, useState } from 'react';

import { Center, Loader } from '@mantine/core';
import { useMutation, useQuery } from '@tanstack/react-query';
import type { Entity } from '@teo/backend';

import { readOf } from '../lib/readOf';
import { trpc } from '../lib/trpc';
import { id } from '../utils/entityUtils';
import logger from '../utils/logger';
import { useAuth } from './AuthContext';

interface EntityContextValue {
  selectedEntity: Entity | null;
  setSelectedEntity: (entity: Entity) => void;
  isInitializing: boolean;
}

const EntityContext = createContext<EntityContextValue | undefined>(undefined);

export function EntityProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [selectedEntity, setSelectedEntityState] = useState<Entity | null>(null);
  const manuallySelectedEntityRef = useRef<Entity | null>(null);
  const entitiesQuery = useQuery({ ...trpc.entities.getEntities.queryOptions(), enabled: !!user });
  const selectionQuery = useQuery({
    ...trpc.entities.getSelectedEntity.queryOptions(),
    enabled: !!user,
  });
  const persistSelection = useMutation({
    ...trpc.entities.setSelectedEntity.mutationOptions(),
    onError: (error) => {
      logger.error({ error }, 'Failed to persist selected entity');
    },
  });
  const persistEntitySelection = persistSelection.mutate;
  // A failed refetch keeps the data that was loaded. The read stays `ready`, so a refetch that
  // fails does not drop the saved selection and move the header to the first ownership.
  const entitiesRead = readOf(entitiesQuery);
  const selectionRead = readOf(selectionQuery);
  const entities = entitiesRead.kind === 'ready' ? entitiesRead.data : null;
  const selectionKind = selectionRead.kind;
  const savedEntityId = selectionRead.kind === 'ready' ? selectionRead.data : null;

  useEffect(() => {
    if (entities === null || selectionKind === 'loading') return;
    const manuallySelectedEntity = manuallySelectedEntityRef.current;
    if (manuallySelectedEntity) {
      if (entities.some((entity) => id(entity) === id(manuallySelectedEntity))) return;
      manuallySelectedEntityRef.current = null;
    }
    const saved = savedEntityId
      ? entities.find((entity) => id(entity) === savedEntityId)
      : undefined;
    const resolved = saved ?? entities[0] ?? null;
    setSelectedEntityState(resolved);
    if (selectionKind === 'ready' && resolved && id(resolved) !== savedEntityId) {
      persistEntitySelection({ entityId: id(resolved) });
    }
  }, [entities, persistEntitySelection, savedEntityId, selectionKind]);

  const isInitializing =
    !!user &&
    entitiesRead.kind !== 'failed' &&
    (entitiesRead.kind === 'loading' || selectionRead.kind === 'loading');
  const setSelectedEntity = (entity: Entity) => {
    manuallySelectedEntityRef.current = entity;
    setSelectedEntityState(entity);
    persistEntitySelection({ entityId: id(entity) });
  };

  if (isInitializing) {
    return (
      <Center h="100vh" aria-label="Loading entity selection">
        <Loader />
      </Center>
    );
  }

  return (
    <EntityContext.Provider value={{ selectedEntity, setSelectedEntity, isInitializing }}>
      {children}
    </EntityContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useEntity() {
  const context = useContext(EntityContext);
  if (!context) {
    throw new Error('useEntity must be used within EntityProvider');
  }
  return context;
}
